import { useState, useEffect } from 'react';
import api from '../../lib/api';

interface ChecklistItem {
  id: string;
  label: string;
  done: boolean;
}

interface Props {
  hasMundus: boolean;
  hasMessages: boolean;
  mundusId?: string;
}

export default function OnboardingChecklist({ hasMundus, hasMessages, mundusId }: Props) {
  const [waLinked, setWaLinked] = useState(false);
  const [hasWin, setHasWin] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('onboarding_checklist_dismissed')) {
      setDismissed(true);
      return;
    }
    checkStatuses();
  }, [hasMundus, hasMessages, mundusId]);

  const checkStatuses = async () => {
    try {
      const waRes = await api.get('/api/gateway/whatsapp/status');
      setWaLinked(waRes.data.linked);
    } catch {}

    if (mundusId) {
      try {
        const winsRes = await api.get(`/api/mundus/${mundusId}/wins`);
        const completed = winsRes.data.recentCompleted || [];
        setHasWin(completed.length > 0);
      } catch {}
    }
  };

  const items: ChecklistItem[] = [
    { id: 'world', label: 'Create your world', done: hasMundus },
    { id: 'chat', label: 'Chat with Atento', done: hasMessages },
    { id: 'whatsapp', label: 'Connect WhatsApp', done: waLinked },
    { id: 'win', label: 'Complete first win', done: hasWin },
  ];

  const doneCount = items.filter(i => i.done).length;
  const allDone = doneCount === items.length;

  // Auto-dismiss when all done
  useEffect(() => {
    if (allDone && !dismissed) {
      const timer = setTimeout(() => {
        localStorage.setItem('onboarding_checklist_dismissed', '1');
        setDismissed(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [allDone]);

  if (dismissed) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900">
          Getting Started ({doneCount}/{items.length})
        </h3>
        {allDone && (
          <span className="text-xs text-green-600 font-medium">All done!</span>
        )}
      </div>

      {/* Progress bar */}
      <div className="w-full bg-slate-100 rounded-full h-1.5 mb-3">
        <div
          className="h-1.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
          style={{ width: `${(doneCount / items.length) * 100}%` }}
        />
      </div>

      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-2.5">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
              item.done
                ? 'bg-green-100 text-green-600'
                : 'bg-slate-100 text-slate-300'
            }`}>
              {item.done ? (
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : (
                <div className="w-2 h-2 rounded-full bg-slate-300" />
              )}
            </div>
            <span className={`text-sm ${item.done ? 'text-slate-500 line-through' : 'text-slate-700'}`}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
